<template>
  <section>
    <div class="w-full flex justify-start">
      <slot name="header" />
    </div>

    <Dialog
      v-model:visible="formVisibility"
      :draggable="false"
      :modal="true"
      :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
      :style="{ width: '50vw' }"
    >
      <template #header>
        <h5 class="w-full text-center">{{ $t("classes.event.add") }}</h5>
      </template>

      <FormMeeting
        v-model:eventForm="eventForm"
        v-model:isExternal="isExternal"
        :vuelidate$="v$"
        @onClose="closeForm()"
        @onSubmit="submitForm()"
        :loading="loading"
      />
    </Dialog>

    <TableAgenda :eventCreated="eventCreated" />
  </section>
</template>

<script lang="ts">
import { maxLength, minLength, or, required } from "@/locale/locale.config";
import { EventForm } from "@/models/agenda.model";
import { AgendaRest } from "@/services/rest/agenda.rest";
import { Messages } from "@/utils/messages.util";
import { validTime } from "@/utils/regex.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import useVuelidate from "@vuelidate/core";
import { defineAsyncComponent, defineComponent } from "vue";
import { AgendaMapper } from "./agenda.mapper";
import { AgendaService } from "./agenda.service";
import { AgendaUtils } from "./agenda.util";

const TableAgenda = defineAsyncComponent(() => import("./table.vue"));

const FormMeeting = defineAsyncComponent(() => import("./form-event.vue"));

export default defineComponent({
  name: "agenda",
  components: {
    TableAgenda,
    FormMeeting,
  },
  data() {
    return {
      eventForm: new EventForm(),
      eventCreated: false as boolean,
      loading: false,
      isExternal: false as boolean,
    };
  },
  props: {
    formVisible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:formVisible"],
  computed: {
    rest(): AgendaRest {
      return new AgendaRest();
    },
    service(): AgendaService {
      return new AgendaService();
    },
    mapper(): AgendaMapper {
      return new AgendaMapper();
    },
    agendaUtils(): AgendaUtils {
      return new AgendaUtils();
    },
    formVisibility: {
      get() {
        return this.formVisible;
      },
      set(value) {
        if (this.formVisible) this.closeForm();
        this.$emit("update:formVisible", value);
      },
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    const isAfterStartTime = () => {
      return this.eventForm.endTime > this.eventForm.startTime;
    };

    const isValidInterval = (value) => {
      const timeToMinutes = (time: string): number => {
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
      };

      const startMinutes = timeToMinutes(this.eventForm.startTime);
      const endMinutes = timeToMinutes(value);
      return endMinutes - startMinutes <= 240;
    };

    const isAfterNow = () => {
      const startDate = new Date(this.eventForm.date!);
      const today = new Date();
      const [hours, mins] = this.eventForm.startTime.split(":");
      const offSet = new Date().getTimezoneOffset() / 60;
      startDate?.setHours(Number(hours) - offSet, Number(mins));
      today.setHours(today.getHours() - offSet, today.getMinutes());
      return startDate! >= today ? true : false;
    };
    return {
      eventForm: {
        classroom: {
          required,
        },
        course: {
          required,
        },
        name: {
          required,
          min: minLength(2),
          max: maxLength(50),
        },
        date: {
          required,
          validDate: or(isAfterNow),
        },
        startTime: {
          required,
          validTime: or((value: string) => {
            return validTime(value);
          }),
          validStartTime: or(isAfterNow),
        },
        endTime: {
          required,
          validTime: or((value: string) => {
            return validTime(value);
          }),
          validEndTime: or(isAfterStartTime),
          validTimeInterval: or(isValidInterval),
        },
        url: {
          validUrl: this.isExternal
            ? or((value: string) => {
                return this.agendaUtils.isValidUrl(value);
              })
            : "",
        },
      },
    };
  },
  methods: {
    submitForm(): void {
      this.v$.eventForm.$touch();
      if (this.v$.eventForm.$invalid) return;
      this.createEvent();
    },
    createEvent(): void {
      this.loading = true;
      const data = this.mapper.eventToPersistence(this.eventForm);
      this.rest.addAgenda(data).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.EVENT_CREATE_SUCCESS));
          this.closeForm();
          this.eventCreated = true;
          this.loading = false;
        },
        error: () => {
          this.$toast.add(ToastFactory.error(Messages.EVENT_CREATE_ERROR));
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
    },
    closeForm(): void {
      this.v$.$reset();
      Object.assign(this.eventForm, new EventForm());
      this.$emit("update:formVisible", false);
    },
  },
});
</script>

<style lang="scss">
@import "./style.scss";
</style>
